// Hand-drawn SVG/HTML charts for the dealer analytics: no chart library in
// this bundle, and these need only a few shapes. Every chart has a hover (and
// keyboard focus) readout, and every value it shows is also printed somewhere
// on the page, so the tooltip never gates a number.

import { useLayoutEffect, useRef, useState } from "react";
import styles from "./DealerAnalytics.module.scss";
import { US_STATE_TILES } from "./usStates";

// ---- Helpers ---------------------------------------------------------------

const useWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
};

// A round top for the y axis and the steps between, e.g. 0 / 25 / 50 / 75 / 100.
const niceScale = (max, ticks = 4) => {
  if (!(max > 0)) return { top: ticks, step: 1 };
  const rough = max / ticks;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const n = rough / pow;
  let step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * pow;
  if (step < 1) step = 1;
  return { top: Math.ceil(max / step) * step, step };
};

// A bar with a 4px rounded data end and a square base.
const columnPath = (x, y, w, h) => {
  if (h <= 0) return "";
  const r = Math.min(4, h, w / 2);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
};

const Tooltip = ({ x, y, title, rows }) => (
  <div className={styles.tooltip} style={{ left: x, top: y }} role="status">
    {title && <div className={styles.tooltipTitle}>{title}</div>}
    {rows.map((r) => (
      <div key={r.label} className={styles.tooltipRow}>
        {r.color && <span className={r.kind === "line" ? styles.lineKey : styles.swatch} style={{ background: r.color }} />}
        <strong>{r.value}</strong>
        <span>{r.label}</span>
      </div>
    ))}
  </div>
);

// Keep a tooltip inside its chart horizontally.
const clampX = (x, width, margin = 70) => Math.min(Math.max(x, margin), Math.max(margin, width - margin));

export const Legend = ({ items }) => (
  <div className={styles.legend}>
    {items.map((i) => (
      <span key={i.label} className={styles.legendItem}>
        <span className={i.kind === "line" ? styles.lineKey : styles.swatch} style={{ background: i.color }} />
        {i.label}
      </span>
    ))}
  </div>
);

// ---- Sparkline -------------------------------------------------------------

export const Sparkline = ({ values }) => {
  const w = 120;
  const h = 28;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(h - 2 - (v / max) * (h - 4)).toFixed(1)}`).join(" ");
  return (
    <svg className={styles.spark} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" stroke="var(--da-s1)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// ---- Trend: this period against the one before -----------------------------

/**
 * points / previous: [{ label, value }] of equal length, oldest first. The
 * current period is the accent line with a wash; the previous period a muted
 * line on the same axis (same measure, so one scale is honest).
 */
export const TrendChart = ({ points, previous, format = (v) => v, currentLabel, previousLabel, height = 230 }) => {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const pad = { top: 14, right: 12, bottom: 26, left: 44 };
  const innerW = Math.max(10, width - pad.left - pad.right);
  const innerH = height - pad.top - pad.bottom;
  const n = points.length;
  const max = Math.max(0, ...points.map((p) => p.value), ...previous.map((p) => p.value));
  const { top, step } = niceScale(max);
  const x = (i) => pad.left + (n <= 1 ? innerW / 2 : (i * innerW) / (n - 1));
  const y = (v) => pad.top + innerH - (v / top) * innerH;
  const line = (list) => list.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join("");
  const area = n > 1 ? `${line(points)}L${x(n - 1)},${y(0)}L${x(0)},${y(0)}Z` : "";
  const ticks = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(v);
  // Every few days, plus the last day; a regular label too close to the last
  // one gives way to it.
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(innerW / 64))));
  const labelled = new Set();
  for (let i = 0; i < n; i += labelEvery) labelled.add(i);
  for (const i of labelled) if (i !== n - 1 && x(n - 1) - x(i) < 56) labelled.delete(i);
  if (n) labelled.add(n - 1);

  const indexAt = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const rel = clientX - rect.left - pad.left;
    return Math.min(n - 1, Math.max(0, Math.round((rel / innerW) * (n - 1))));
  };
  const onKey = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); setHover((h) => (h === null ? n - 1 : Math.min(n - 1, h + 1))); }
    if (e.key === "ArrowLeft") { e.preventDefault(); setHover((h) => (h === null ? n - 1 : Math.max(0, h - 1))); }
    if (e.key === "Escape") setHover(null);
  };

  return (
    <div
      ref={ref}
      className={styles.chartWrap}
      tabIndex={0}
      role="img"
      aria-label={`${currentLabel}: ${points.map((p) => `${p.label} ${format(p.value)}`).join(", ")}`}
      onKeyDown={onKey}
      onBlur={() => setHover(null)}
    >
      {width > 0 && (
        <svg width={width} height={height} onPointerMove={(e) => setHover(indexAt(e.clientX))} onPointerLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={pad.left + innerW} y1={y(t)} y2={y(t)} stroke={t === 0 ? "var(--da-axis)" : "var(--da-grid)"} strokeWidth="1" />
              <text className={styles.axisText} x={pad.left - 8} y={y(t) + 4} textAnchor="end">{format(t)}</text>
            </g>
          ))}
          {points.map((p, i) => labelled.has(i) ? (
            <text key={p.label} className={styles.axisText} x={x(i)} y={height - 6} textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}>{p.label}</text>
          ) : null)}
          {previous.length > 1 && <path d={line(previous)} fill="none" stroke="var(--da-prev)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
          {n > 1 && <path d={area} fill="var(--da-s1-wash)" />}
          <path d={line(points)} fill="none" stroke="var(--da-s1)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {n > 0 && hover === null && (
            <circle cx={x(n - 1)} cy={y(points[n - 1].value)} r="4" fill="var(--da-s1)" stroke="var(--da-card)" strokeWidth="2" />
          )}
          {hover !== null && (
            <g>
              <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + innerH} stroke="var(--da-axis)" strokeWidth="1" />
              {previous[hover] && <circle cx={x(hover)} cy={y(previous[hover].value)} r="4" fill="var(--da-prev)" stroke="var(--da-card)" strokeWidth="2" />}
              <circle cx={x(hover)} cy={y(points[hover].value)} r="4" fill="var(--da-s1)" stroke="var(--da-card)" strokeWidth="2" />
            </g>
          )}
        </svg>
      )}
      {hover !== null && width > 0 && (
        <Tooltip
          x={clampX(x(hover), width)}
          y={y(Math.max(points[hover].value, previous[hover] ? previous[hover].value : 0))}
          title={points[hover].label}
          rows={[
            { label: currentLabel, value: format(points[hover].value), color: "var(--da-s1)", kind: "line" },
            ...(previous[hover] ? [{ label: `${previousLabel} (${previous[hover].label})`, value: format(previous[hover].value), color: "var(--da-prev)", kind: "line" }] : []),
          ]}
        />
      )}
    </div>
  );
};

// ---- Horizontal bar list -----------------------------------------------------

/** rows: [{ label, value, note?, title? }] - one series, so one colour. */
export const BarList = ({ rows, format = (v) => v.toLocaleString("en-US"), color = "var(--da-s1)", empty = "Nothing yet." }) => {
  const max = Math.max(0, ...rows.map((r) => r.value));
  if (!rows.length) return <div className={styles.empty}>{empty}</div>;
  return (
    <div className={styles.barList}>
      {rows.map((r) => (
        <div key={r.key || r.label} className={styles.barRow} title={r.title || undefined}>
          <span className={styles.barLabel}>{r.label}</span>
          <span className={styles.barTrack} aria-hidden="true">
            {r.value > 0 && <span className={styles.barFill} style={{ width: `${(r.value / max) * 100}%`, background: color }} />}
          </span>
          <span className={styles.barValue}>
            {format(r.value)}
            {r.note && <span className={styles.barNote}>{r.note}</span>}
          </span>
        </div>
      ))}
    </div>
  );
};

// ---- Grouped columns -------------------------------------------------------

/**
 * rows: [{ label, [series.key]: number }]; series: [{ key, label, color }] (at
 * most three: the palette is checked for three side by side).
 */
export const ColumnChart = ({ rows, series, format = (v) => v, height = 230 }) => {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const pad = { top: 14, right: 8, bottom: 30, left: 50 };
  const innerW = Math.max(10, width - pad.left - pad.right);
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(0, ...rows.flatMap((r) => series.map((s) => r[s.key] || 0)));
  const { top, step } = niceScale(max);
  const band = innerW / Math.max(1, rows.length);
  const gap = 2;
  const barW = Math.max(3, Math.min(24, (band * 0.72 - gap * (series.length - 1)) / series.length));
  const groupW = barW * series.length + gap * (series.length - 1);
  const y = (v) => pad.top + innerH - (v / top) * innerH;
  const ticks = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(v);
  // Every category labelled while there is room (about 40px each).
  const labelEvery = Math.max(1, Math.ceil(40 / band));

  return (
    <div ref={ref} className={styles.chartWrap}>
      {width > 0 && (
        <svg width={width} height={height} onPointerLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={pad.left} x2={pad.left + innerW} y1={y(t)} y2={y(t)} stroke={t === 0 ? "var(--da-axis)" : "var(--da-grid)"} strokeWidth="1" />
              <text className={styles.axisText} x={pad.left - 8} y={y(t) + 4} textAnchor="end">{format(t)}</text>
            </g>
          ))}
          {rows.map((r, i) => {
            const gx = pad.left + band * i + (band - groupW) / 2;
            return (
              <g key={r.label}>
                {i % labelEvery === 0 && (
                  <text className={styles.axisText} x={pad.left + band * i + band / 2} y={height - 8} textAnchor="middle">{r.label}</text>
                )}
                {series.map((s, si) => {
                  const v = r[s.key] || 0;
                  const bx = gx + si * (barW + gap);
                  return <path key={s.key} d={columnPath(bx, y(v), barW, y(0) - y(v))} fill={s.color} opacity={hover !== null && hover !== i ? 0.45 : 1} />;
                })}
                <rect
                  x={pad.left + band * i}
                  y={pad.top}
                  width={band}
                  height={innerH}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={`${r.label}: ${series.map((s) => `${s.label} ${format(r[s.key] || 0)}`).join(", ")}`}
                  onPointerEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>
      )}
      {hover !== null && width > 0 && (
        <Tooltip
          x={clampX(pad.left + band * hover + band / 2, width)}
          y={y(Math.max(...series.map((s) => rows[hover][s.key] || 0)))}
          title={rows[hover].label}
          rows={series.map((s) => ({ label: s.label, value: format(rows[hover][s.key] || 0), color: s.color }))}
        />
      )}
    </div>
  );
};

// ---- Sequential colour -------------------------------------------------------

const SEQ = ["var(--da-seq-1)", "var(--da-seq-2)", "var(--da-seq-3)", "var(--da-seq-4)", "var(--da-seq-5)", "var(--da-seq-6)"];
// Steps 4 and up are dark enough in light mode to need white text on them.
const seqIndex = (value, max) => (value > 0 && max > 0 ? Math.min(SEQ.length - 1, Math.floor((value / max) * SEQ.length - 1e-9)) : -1);
const seqFill = (value, max) => {
  const i = seqIndex(value, max);
  return i < 0 ? "var(--da-seq-0)" : SEQ[i];
};

export const SeqScale = ({ max, format = (v) => v }) => (
  <div className={styles.scale} aria-hidden="true">
    <span>0</span>
    <span className={styles.step} style={{ background: "var(--da-seq-0)" }} />
    {SEQ.map((c) => <span key={c} className={styles.step} style={{ background: c }} />)}
    <span>{format(max)}</span>
  </div>
);

// ---- Weekday x hour heatmap ------------------------------------------------

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const hourLabel = (h) => (h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`);

/** grid[weekday 0=Sun][hour 0-23] */
export const Heatmap = ({ grid, unit = "leads" }) => {
  const [hover, setHover] = useState(null);
  const max = Math.max(0, ...grid.flat());
  return (
    <div className={styles.chartWrap} onPointerLeave={() => setHover(null)}>
      <div className={styles.heatmap}>
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} style={{ textAlign: "center" }}>{h % 3 === 0 ? hourLabel(h) : ""}</span>
        ))}
        {grid.map((row, d) => (
          <HeatRow key={WEEKDAYS[d]} day={d} row={row} max={max} onHover={setHover} />
        ))}
      </div>
      <SeqScale max={max} />
      {hover && (
        <div className={styles.tooltip} style={{ left: hover.x, top: hover.y }} role="status">
          <div className={styles.tooltipTitle}>{WEEKDAYS[hover.d]} {hourLabel(hover.h)}–{hourLabel((hover.h + 1) % 24)} ET</div>
          <div className={styles.tooltipRow}><strong>{hover.v}</strong><span>{unit}</span></div>
        </div>
      )}
    </div>
  );
};

const HeatRow = ({ day, row, max, onHover }) => (
  <>
    <span className={styles.heatLabel}>{WEEKDAYS[day]}</span>
    {row.map((v, h) => (
      <span
        key={h}
        className={styles.heatCell}
        style={{ background: seqFill(v, max) }}
        onPointerEnter={(e) => {
          const cell = e.currentTarget.getBoundingClientRect();
          const box = e.currentTarget.closest(`.${styles.chartWrap}`).getBoundingClientRect();
          onHover({ d: day, h, v, x: cell.left - box.left + cell.width / 2, y: cell.top - box.top });
        }}
      />
    ))}
  </>
);

// ---- US state tile map -------------------------------------------------------

/** values: { [stateCode]: number } */
export const StateTileMap = ({ values, unit, onSelect, selected }) => {
  const [hover, setHover] = useState(null);
  const ref = useRef(null);
  const max = Math.max(0, ...Object.values(values));
  return (
    <div ref={ref} className={styles.chartWrap} onPointerLeave={() => setHover(null)}>
      <div className={styles.tileMap}>
        {US_STATE_TILES.map(({ code, name, row, col }) => {
          const v = values[code] || 0;
          const i = seqIndex(v, max);
          const show = (e) => {
            const cell = e.currentTarget.getBoundingClientRect();
            const box = ref.current.getBoundingClientRect();
            setHover({ code, name, v, x: cell.left - box.left + cell.width / 2, y: cell.top - box.top });
          };
          return (
            <button
              key={code}
              type="button"
              className={styles.stateTile}
              style={{
                gridRow: row + 1,
                gridColumn: col + 1,
                background: seqFill(v, max),
                color: i >= 3 ? "#fff" : "var(--da-ink-2)",
                outline: selected === code ? "2px solid var(--da-ink)" : undefined,
              }}
              aria-label={`${name}: ${v} ${unit}`}
              onPointerEnter={show}
              onFocus={show}
              onBlur={() => setHover(null)}
              onClick={onSelect ? () => onSelect(code) : undefined}
            >
              {code}
            </button>
          );
        })}
      </div>
      <SeqScale max={max} />
      {hover && (
        <div className={styles.tooltip} style={{ left: hover.x, top: hover.y }} role="status">
          <div className={styles.tooltipTitle}>{hover.name}</div>
          <div className={styles.tooltipRow}><strong>{hover.v.toLocaleString("en-US")}</strong><span>{unit}</span></div>
        </div>
      )}
    </div>
  );
};

// ---- Funnel ----------------------------------------------------------------

/**
 * steps: [{ label, value }]. Ordered stages, so an ordinal ramp (lighter to
 * darker) rather than categorical colours. Bars are scaled to the largest
 * step; the ratio to the step before sits between rows.
 */
export const Funnel = ({ steps }) => {
  const ramp = ["var(--da-seq-3)", "var(--da-seq-4)", "var(--da-seq-4)", "var(--da-seq-5)", "var(--da-seq-6)"];
  const max = Math.max(0, ...steps.map((s) => s.value));
  return (
    <div className={styles.funnel}>
      {steps.map((s, i) => (
        <div key={s.label}>
          {i > 0 && (
            <div className={styles.funnelStep}>
              {steps[i - 1].value > 0 ? `${Math.round((s.value / steps[i - 1].value) * 1000) / 10}% of ${steps[i - 1].label.toLowerCase()}` : " "}
            </div>
          )}
          <div className={styles.funnelRow}>
            <span className={styles.barLabel}>{s.label}</span>
            <span className={styles.barTrack} style={{ height: 22 }} aria-hidden="true">
              {s.value > 0 && <span className={styles.funnelBar} style={{ width: `${Math.max(1.5, (s.value / max) * 100)}%`, background: ramp[i] || ramp[ramp.length - 1] }} />}
            </span>
            <span className={styles.barValue}>{s.value.toLocaleString("en-US")}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// Views, saves and leads always wear the same three colours, on every chart.
export const ACTIVITY_SERIES = [
  { key: "views", label: "Car views", color: "var(--da-s1)" },
  { key: "saves", label: "Saves", color: "var(--da-s2)" },
  { key: "leads", label: "Leads", color: "var(--da-s3)" },
];
